// Account.js
import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_BASE_URL } from '../config';
import AccountProfile from "../components/AccountProfile";
import AccountPreferences from "../components/AccountPreferences";
import AccountOrderHistory from "../components/AccountOrderHistory";

function Account() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const tabType = searchParams.get("tab") || "";

  const [authResponse, setAuthResponse] = useState(null);
  const [userResponse, setUserResponse] = useState(null);

  const [interimProfileData, setInterimProfileData] = useState({
    email: "",
    firstName: "",
    lastName: "",
  });

  const [unsavedChanges, setUnsavedChanges] = useState(false);

  // Profile save status: null | "saving" | "saved" | "error"
  const [profileSaveStatus, setProfileSaveStatus] = useState(null);

  // Preferences state
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    marketingEmails: false,
  });
  const [prefSaveStatus, setPrefSaveStatus] = useState(null);

  const logOut = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/auth/logout`,
        {},
        { withCredentials: true }
      );
      if (response.status === 200) {
        navigate("/log-in");
      }
    } catch (error) {
      console.error("API call failed:", error);
    }
  };

  // Verify cookie / token
  useEffect(() => {
    async function checkStatus() {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/auth/verify-token`,
          { withCredentials: true }
        );
        setAuthResponse(response);
      } catch (error) {
        if (error?.response?.status === 401) {
          navigate("/log-in");
        } else {
          console.error("verify-token error:", error);
        }
      }
    }
    checkStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pull user data (cookie-inferred: no userId param)
  useEffect(() => {
    async function pullUserData() {
      try {
        const userData = await axios.get(
          `${API_BASE_URL}/api/auth/request-user`,
          { withCredentials: true }
        );

        setUserResponse(userData);

        setInterimProfileData({
          email: userData?.data?.email ?? "",
          firstName: userData?.data?.firstName ?? "",
          lastName: userData?.data?.lastName ?? "",
        });
      } catch (err) {
        console.error("request-user error:", err);
      }
    }

    // Only call once we know auth check ran
    if (authResponse) pullUserData();
  }, [authResponse]);

  const handleChange = (field) => (e) => {
    // Keep your original behavior: letters only
    const value = e.currentTarget.value.match(/[a-zA-Z]+/g)?.join("") || "";
    setInterimProfileData((prev) => ({ ...prev, [field]: value }));
    setProfileSaveStatus(null);
  };

  const checkUnsavedChanges = useCallback(() => {
    if (!interimProfileData || !userResponse?.data) return;

    const changesFirstName =
      interimProfileData.firstName !== (userResponse.data.firstName || "");
    const changesLastName =
      interimProfileData.lastName !== (userResponse.data.lastName || "");

    setUnsavedChanges(changesFirstName || changesLastName);
  }, [interimProfileData, userResponse]);

  useEffect(() => {
    checkUnsavedChanges();
  }, [checkUnsavedChanges]);

  // Load preferences when userResponse changes (if backend returns them)
  useEffect(() => {
    if (!userResponse?.data) return;
    setPreferences((prev) => ({ ...prev, ...(userResponse.data.preferences || {}) }));
  }, [userResponse]);

  const handlePrefChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setPreferences((p) => ({ ...p, [field]: value }));
    setUnsavedChanges(true);
    setPrefSaveStatus(null);
  };

  const savePreferences = async () => {
    try {
      setPrefSaveStatus("saving");
      const res = await axios.post(
        `${API_BASE_URL}/api/auth/update-preferences`,
        { preferences },
        { withCredentials: true }
      );
      if (res.status === 200) {
        setPrefSaveStatus("saved");
        setUnsavedChanges(false);

        // Update local cached userResponse so it reflects saved prefs
        setUserResponse((prev) => ({
          ...prev,
          data: { ...(prev?.data || {}), preferences },
        }));
      } else {
        setPrefSaveStatus("error");
      }
    } catch (err) {
      console.error("Save preferences error:", err);
      setPrefSaveStatus("error");
    } finally {
      setTimeout(() => setPrefSaveStatus(null), 3000);
    }
  };

  // Save profile (first/last name) -> cookie-inferred route
  const saveProfile = async () => {
    try {
      setProfileSaveStatus("saving");

      const payload = {
        firstName: interimProfileData.firstName,
        lastName: interimProfileData.lastName,
      };

      const res = await axios.post(
        `${API_BASE_URL}/api/auth/update-profile`,
        payload,
        { withCredentials: true }
      );

      // Update userResponse so unsavedChanges becomes false
      setUserResponse((prev) => ({
        ...prev,
        data: {
          ...(prev?.data || {}),
          firstName: res?.data?.user?.firstName ?? payload.firstName,
          lastName: res?.data?.user?.lastName ?? payload.lastName,
        },
      }));

      setProfileSaveStatus("saved");
      setUnsavedChanges(false);
    } catch (err) {
      console.error("Save profile error:", err);
      setProfileSaveStatus("error");
    } finally {
      setTimeout(() => setProfileSaveStatus(null), 3000);
    }
  };

  const updateAccountContent = useCallback(() => {
    if (!userResponse) return <p>Loading...</p>;

    if (tabType === "") {
      return (
        <>
          <h2>
            Welcome, <span className="account-content-span">{userResponse?.data?.firstName}</span>!
          </h2>
          <AccountProfile
            interimProfileData={interimProfileData}
            handleChange={handleChange}
            unsavedChanges={unsavedChanges}
            saveProfile={saveProfile}
            profileSaveStatus={profileSaveStatus}
          />
        </>
      );
    }

    if (tabType === "preferences") {
      return (
        <AccountPreferences
          preferences={preferences}
          handlePrefChange={handlePrefChange}
          savePreferences={savePreferences}
          saveStatus={prefSaveStatus}
          unsavedChanges={unsavedChanges}
        />
      );
    }

    if (tabType === "points") {
      return (
        <p>
          <strong>Coming Soon!</strong> Feature under development. Your points will be added once
          this feature is live.
        </p>
      );
    }

    if (tabType === "orderHistory") return <AccountOrderHistory />;

    return <p>{String(tabType)}</p>;
  }, [
    userResponse,
    tabType,
    interimProfileData,
    unsavedChanges,
    preferences,
    prefSaveStatus,
    profileSaveStatus,
  ]);

  return (
    <section className="account-container" style={{ height: "100%" }}>
      <h1 className="centered-subtitle">Account</h1>

      <article className="account-subcontainer">
        <div className="account-tabs-container">
          {tabType === "" ? (
            <p className="account-tab-selected">
              Profile <i className="bi bi-check"></i>
            </p>
          ) : (
            <Link to="/account" className="account-tab-unselected">
              Profile
            </Link>
          )}

          {tabType === "orderHistory" ? (
            <p className="account-tab-selected">
              Order History <i className="bi bi-check"></i>
            </p>
          ) : (
            <Link to="/account?tab=orderHistory" className="account-tab-unselected">
              Order History
            </Link>
          )}

          {tabType === "points" ? (
            <p className="account-tab-selected">
              Points <i className="bi bi-check"></i>
            </p>
          ) : (
            <Link to="/account?tab=points" className="account-tab-unselected">
              Points
            </Link>
          )}

          {tabType === "preferences" ? (
            <p className="account-tab-selected">
              Preferences <i className="bi bi-check"></i>
            </p>
          ) : (
            <Link to="/account?tab=preferences" className="account-tab-unselected">
              Preferences
            </Link>
          )}

          <button className="account-logout" onClick={logOut}>
            Log Out <i className="bi bi-box-arrow-right"></i>
          </button>
        </div>

        <div className="account-content-container">{updateAccountContent()}</div>
      </article>
    </section>
  );
}

export default Account;
